/**
 * WorkspacePage — iter-438k Phase 6 (demo shell for the DynamicSidebar).
 * ═══════════════════════════════════════════════════════════════════
 * Renders `<DynamicSidebar workspaceKey={:key} />` alongside a hero
 * that summarises the workspace. Serves as a proof-of-concept that
 * `AdminSidebar`'s hardcoded `MENU_SECTIONS` can be fully replaced by
 * API-driven data (`/api/identity/workspace/{key}/nav`).
 *
 * When a user clicks a workspace card on the Home dashboard, they now
 * land here — full sidebar rendered from the server. Adding a
 * permission live updates the sidebar without a redeploy.
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as LucideIcons from 'lucide-react';

import { getToken } from '@/lib/authToken';
import { DashboardLayout } from '@/components/layouts';
import { DynamicSidebar } from '@/components/DynamicSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WorkspacePage = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // iter-438k Phase 7 — fire-and-forget visit hook. Never blocks render;
  // failures are silent (backend never returns 4xx for this endpoint).
  React.useEffect(() => {
    if (!key) return;
    (async () => {
      try {
        await fetch(`${API_URL}/api/identity/workspace-visit`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ workspace_key: key }),
        });
        // Bust the Home page's prefs cache so "Recent" reflects this visit next time.
        queryClient.invalidateQueries({ queryKey: ['identity-preferences'] });
      } catch {
        // Fire-and-forget — never surface an error to the user.
      }
    })();
  }, [key, queryClient]);

  // Fetch the same nav so we can render a hero without duplicating logic.
  const { data, isLoading } = useQuery({
    queryKey: ['workspace-nav', key],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/identity/workspace/${key}/nav`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    staleTime: 30_000,
    enabled: !!key,
    retry: 1,
  });

  const workspace = data?.workspace;
  const Icon = workspace ? (LucideIcons[workspace.icon] || LucideIcons.LayoutGrid) : LucideIcons.Loader2;

  return (
    <DashboardLayout title={workspace?.label || 'Workspace'} showBackButton>
      <div className="flex gap-6 -mx-4 sm:-mx-6 lg:-mx-8" data-testid={`workspace-page-${key}`}>
        {/* Left rail — the dynamic sidebar */}
        <div className="hidden md:block flex-shrink-0">
          <DynamicSidebar workspaceKey={key} />
        </div>

        {/* Right pane — workspace hero + quick actions */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-2">
          {isLoading ? (
            <div className="text-slate-400 flex items-center gap-2">
              <LucideIcons.Loader2 className="h-4 w-4 animate-spin" /> Loading workspace…
            </div>
          ) : workspace ? (
            <>
              {/* Hero */}
              <div className="mb-6 flex items-start gap-4">
                <div
                  className="h-14 w-14 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${workspace.color || '#3b82f6'}15` }}
                >
                  <Icon className="h-6 w-6" style={{ color: workspace.color || '#3b82f6' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold text-slate-900">{workspace.label}</h1>
                  {workspace.description && (
                    <p className="text-slate-600 mt-1">{workspace.description}</p>
                  )}
                </div>
                {workspace.route && workspace.route !== `/workspace/${key}` && (
                  <Button
                    size="sm"
                    onClick={() => navigate(workspace.route)}
                    data-testid="workspace-page-open-default-btn"
                  >
                    Open workspace <LucideIcons.ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>

              {/* Info card */}
              <Card>
                <CardContent className="py-6">
                  <div className="flex items-start gap-3 text-slate-600">
                    <LucideIcons.Info className="h-5 w-5 flex-shrink-0 text-slate-400 mt-0.5" />
                    <div className="text-sm space-y-2">
                      <p>
                        <span className="font-medium text-slate-800">Dynamic navigation.</span> This
                        menu is generated live from the Identity Service. Adding a permission or
                        role assignment updates the sidebar without a redeploy.
                      </p>
                      <p className="text-slate-500">
                        Pick an item from the sidebar to jump into the workspace, or use the button
                        above to go to the workspace&apos;s default landing.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <LucideIcons.SearchX className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-700">Workspace not found or not accessible.</p>
                <Button className="mt-4" onClick={() => navigate('/home')}>
                  Back to Home
                </Button>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </DashboardLayout>
  );
};

export default WorkspacePage;
